import React, {
  useEffect,
  useState
} from "react";

import { createPortal } from "react-dom";
import { useAuth } from "./AuthContext";
import { cognitoLogin } from "../helpers/cognitoLogin";

import {
  subscribeSession,
  lockEnterpriseSession,
  unlockEnterpriseSession,
  ensureEnterpriseSession,
  markSessionActivity,
  isIdle,
  currentIdentity,
  readClaims,
  saveEnterpriseTokens
} from "./enterpriseSession";

export default function SessionGuardian() {
  const { setTokens } = useAuth();

  const [locked, setLocked] = useState(false);
  const [user, setUser] = useState(currentIdentity);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return subscribeSession(value => {
      setLocked(value);

      if (value) {
        setUser(currentIdentity());
      }
    });
  }, []);

  useEffect(() => {
    function activity() {
      if (!isIdle()) {
        markSessionActivity();
      } else {
        lockEnterpriseSession();
      }
    }

    const events = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll"
    ];

    events.forEach(event => {
      window.addEventListener(event, activity, {
        passive: true
      });
    });

    async function check() {
      if (isIdle()) {
        lockEnterpriseSession();
        return;
      }

      try {
        await ensureEnterpriseSession();
      } catch {
        // El administrador central gestiona el bloqueo.
      }
    }

    const timer = window.setInterval(check, 10000);

    function onVisible() {
      if (!document.hidden) check();
    }

    document.addEventListener(
      "visibilitychange",
      onVisible
    );

    check();

    return () => {
      window.clearInterval(timer);

      events.forEach(event => {
        window.removeEventListener(event, activity);
      });

      document.removeEventListener(
        "visibilitychange",
        onVisible
      );
    };
  }, []);

  useEffect(() => {
    if (!locked) return;

    const root = document.getElementById("root");
    if (!root) return;

    const previous = root.inert;
    root.inert = true;

    const input = document.getElementById(
      "ip-enterprise-password"
    );

    input?.focus();

    return () => {
      root.inert = previous;
    };
  }, [locked]);

  async function unlock(event) {
    event.preventDefault();

    if (!password || !user.username || busy) return;

    setBusy(true);
    setError("");

    try {
      const result = await cognitoLogin(
        user.username,
        password
      );

      const claims = readClaims(result.idToken);

      if (
        !user.sub ||
        claims.sub !== user.sub
      ) {
        throw new Error(
          "La identidad no coincide."
        );
      }

      saveEnterpriseTokens(result);

      setTokens({
        accessToken: result.accessToken,
        idToken: result.idToken
      });

      setPassword("");
      unlockEnterpriseSession();

    } catch (e) {
      setError(
        e?.message ||
        "No fue posible validar las credenciales."
      );
    } finally {
      setBusy(false);
    }
  }

  if (!locked) return null;

  return createPortal(
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 2147483647,
      background: "rgba(15, 23, 42, 0.78)",
      backdropFilter: "blur(6px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ip-enterprise-title"
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#ffffff",
          borderRadius: 18,
          overflow: "hidden",
          boxShadow: "0 28px 90px rgba(15, 23, 42, 0.28)",
          border: "1px solid rgba(148, 163, 184, 0.20)"
        }}
      >
        <div style={{
          height: 6,
          background: "linear-gradient(90deg, #0f3970 0%, #1d4f91 100%)"
        }} />

        <div style={{
          padding: 34
        }}>
          <div style={{
            marginBottom: 26
          }}>
            <div style={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.02em",
              color: "#0f172a",
              marginBottom: 4
            }}>
              IP DEPOT
            </div>

            <div style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "#64748b"
            }}>
              Control de Inventario
            </div>
          </div>

          <div style={{
            marginBottom: 28
          }}>
            <h2
              id="ip-enterprise-title"
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2
              }}
            >
              Sesión bloqueada
            </h2>

            <p style={{
              margin: "10px 0 0 0",
              fontSize: 14,
              lineHeight: 1.6,
              color: "#475569"
            }}>
              La sesión requiere validación para continuar.
              Ingresa nuevamente tu contraseña.
            </p>
          </div>

          <form onSubmit={unlock}>
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#64748b",
                  marginBottom: 8
                }}
              >
                Usuario
              </label>

              <div style={{
                minHeight: 48,
                display: "flex",
                alignItems: "center",
                padding: "0 14px",
                borderRadius: 10,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                color: "#0f172a",
                fontSize: 14,
                fontWeight: 500,
                overflowWrap: "anywhere"
              }}>
                {user.display || "Usuario no identificado"}
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label
                htmlFor="ip-enterprise-password"
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#64748b",
                  marginBottom: 8
                }}
              >
                Contraseña
              </label>

              <input
                id="ip-enterprise-password"
                type="password"
                value={password}
                autoComplete="current-password"
                disabled={busy || !user.username}
                onChange={e => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: 48,
                  padding: "0 14px",
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#0f172a",
                  fontSize: 14,
                  outline: "none"
                }}
              />
            </div>

            {error && (
              <div style={{
                marginBottom: 18,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#b91c1c",
                fontSize: 13,
                lineHeight: 1.5
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !password || !user.username}
              style={{
                width: "100%",
                height: 48,
                border: 0,
                borderRadius: 10,
                background: "linear-gradient(90deg, #0f3970 0%, #174b89 100%)",
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.01em",
                cursor: busy ? "wait" : "pointer",
                opacity: busy || !user.username ? 0.65 : 1,
                transition: "opacity 0.2s ease"
              }}
            >
              {busy
                ? "Validando credenciales..."
                : "Desbloquear sesión"}
            </button>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}